import { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useCouponDetailQuery } from '../queries/coupon/useCouponDetailQuery';
import { useCouponUsageHistoryQuery } from '../queries/coupon/useCouponUsageHistoryQuery';
import { useCouponUnUsageHistoryQuery } from '../queries/coupon/useCouponUnUsageHistoryQuery';
import { useCouponProductLinkQuery } from '../queries/coupon/useCouponProductLinkQuery';
import { useCouponBrandLinkQuery } from '../queries/coupon/useCouponBrandLinkQuery';
import { CouponApiV2 } from '../../lib/api/coupon/CouponApiV2';
import { excelDownLoad } from '../../lib/common';
import toastMsg from '../../lib/toastMsg';
import couponToastMsg from '../../constants/service/toastMsg/couponToastMsg';
import { setApplyCouponDiscount } from '../../lib/utils/couponUtils';
import { couponActions } from '../../store/modules/coupon';
import { appActions } from '../../store/modules/app';
import { LIST_PAGINATION_MODEL, _LIST_PAGINATION_MODEL } from '../../store/modules/app/models';

/** 초기 필터/페이지네이션 상수 */
const INITIAL_USAGE_HISTORY = {
  filterOption: { userName: null, userAccount: null, orderItemId: null } as Record<string, unknown>,
  sortOption: [{}] as unknown as Record<string, unknown>,
  pagination: { pageNum: LIST_PAGINATION_MODEL.pageNum, pageSize: LIST_PAGINATION_MODEL.pageSize },
};

const INITIAL_UN_USAGE_HISTORY = {
  filterOption: { userName: null, userAccount: null } as Record<string, unknown>,
  pagination: { pageNum: _LIST_PAGINATION_MODEL.pageNum, pageSize: _LIST_PAGINATION_MODEL.pageSize },
};

const INITIAL_PRODUCT_LINK = {
  filterOption: {
    storeId: null, brandName: null, productId: null, productName: null, mainCategory: null, subCategory: null,
  } as Record<string, unknown>,
  pagination: { pageNum: _LIST_PAGINATION_MODEL.pageNum, pageSize: _LIST_PAGINATION_MODEL.pageSize },
};

const INITIAL_BRAND_LINK = {
  filterOption: { storeId: null, storeName: null } as Record<string, unknown>,
  pagination: { pageNum: _LIST_PAGINATION_MODEL.pageNum, pageSize: _LIST_PAGINATION_MODEL.pageSize },
};

export const useCouponDetail = (couponId: number) => {
  const dispatch = useDispatch();

  // 사용자 인터랙션 추적 (인터랙션 전에는 detailQuery 데이터 사용, 후에는 서브 쿼리 사용)
  const [usageHistoryActive, setUsageHistoryActive] = useState(false);
  const [unUsageHistoryActive, setUnUsageHistoryActive] = useState(false);
  const [productLinkActive, setProductLinkActive] = useState(false);
  const [brandLinkActive, setBrandLinkActive] = useState(false);

  // 서브 리스트 옵션 상태
  const [usageHistoryOpt, setUsageHistoryOpt] = useState(INITIAL_USAGE_HISTORY);
  const [unUsageHistoryOpt, setUnUsageHistoryOpt] = useState(INITIAL_UN_USAGE_HISTORY);
  const [productLinkOpt, setProductLinkOpt] = useState(INITIAL_PRODUCT_LINK);
  const [brandLinkOpt, setBrandLinkOpt] = useState(INITIAL_BRAND_LINK);

  // 메인 상세 정보 쿼리 (couponInfo, couponStatistics + 서브 리스트 초기 데이터 모두 반환)
  const detailQuery = useCouponDetailQuery(couponId, {
    couponUsageHistory: INITIAL_USAGE_HISTORY,
    couponUnUsageHistory: INITIAL_UN_USAGE_HISTORY,
    couponProductLink: INITIAL_PRODUCT_LINK,
    couponBrandLink: INITIAL_BRAND_LINK,
  });

  const couponInfo = detailQuery.data?.couponInfo ?? {} as Record<string, any>;
  const couponStatistics = detailQuery.data?.couponStatistics ?? {};

  // 서브 리스트 쿼리들 (사용자 인터랙션 후에만 활성화)
  const usageHistoryQuery = useCouponUsageHistoryQuery({
    couponId, ...usageHistoryOpt,
  }, { enabled: usageHistoryActive && !!couponId });

  const unUsageHistoryQuery = useCouponUnUsageHistoryQuery({
    couponId, ...unUsageHistoryOpt,
  }, { enabled: unUsageHistoryActive && !!couponId });

  const productLinkQuery = useCouponProductLinkQuery({
    couponId,
    discountType: couponInfo.discountType,
    discountAmount: couponInfo.discountAmount,
    maxDiscountPrice: couponInfo.maxDiscountPrice,
    ...productLinkOpt,
  }, { enabled: productLinkActive && !!couponId });

  const brandLinkQuery = useCouponBrandLinkQuery({
    couponId,
    minimumPurchasePrice: couponInfo.minimumPurchasePrice,
    discountType: couponInfo.discountType,
    discountAmount: couponInfo.discountAmount,
    maxDiscountPrice: couponInfo.maxDiscountPrice,
    ...brandLinkOpt,
  }, { enabled: brandLinkActive && !!couponId });

  // detailQuery에서 서브 리스트 초기 데이터를 뷰 컴포넌트가 기대하는 형태로 변환
  const initialUsageHistory = useMemo(() => {
    const raw = detailQuery.data?.couponUsageHistory as any;
    if (!raw) return null;
    const totalCnt = raw.totalCnt ?? 0;
    return {
      list: raw.couponUsageHistoryList ?? [],
      totalCnt,
      summary: { totalCnt },
      filterOption: INITIAL_USAGE_HISTORY.filterOption,
      sortOption: INITIAL_USAGE_HISTORY.sortOption,
      pagination: { ...INITIAL_USAGE_HISTORY.pagination, pages: Math.ceil(totalCnt / INITIAL_USAGE_HISTORY.pagination.pageSize) || 1, totalCnt },
    };
  }, [detailQuery.data?.couponUsageHistory]);

  const initialUnUsageHistory = useMemo(() => {
    const raw = detailQuery.data?.couponUnUsageHistory as any;
    if (!raw) return null;
    const totalCnt = raw.totalCnt ?? 0;
    return {
      list: raw.couponUnUsageHistoryList ?? [],
      totalCnt,
      filterOption: INITIAL_UN_USAGE_HISTORY.filterOption,
      pagination: { ...INITIAL_UN_USAGE_HISTORY.pagination, pages: Math.ceil(totalCnt / INITIAL_UN_USAGE_HISTORY.pagination.pageSize) || 1, totalCnt },
    };
  }, [detailQuery.data?.couponUnUsageHistory]);

  const initialProductLink = useMemo(() => {
    const raw = detailQuery.data?.couponProductLink as any;
    if (!raw) return null;
    const totalCnt = raw.totalCnt ?? 0;
    let list = raw.couponProductLinkList ?? [];
    if (couponInfo.discountType != null && couponInfo.discountAmount != null) {
      list = setApplyCouponDiscount(list, couponInfo.discountType, couponInfo.discountAmount);
    }
    return {
      list,
      totalCnt,
      filterOption: INITIAL_PRODUCT_LINK.filterOption,
      pagination: { ...INITIAL_PRODUCT_LINK.pagination, pages: Math.ceil(totalCnt / INITIAL_PRODUCT_LINK.pagination.pageSize) || 1, totalCnt },
    };
  }, [detailQuery.data?.couponProductLink, couponInfo.discountType, couponInfo.discountAmount]);

  const initialBrandLink = useMemo(() => {
    const raw = detailQuery.data?.couponBrandLink as any;
    if (!raw) return null;
    const totalCnt = raw.totalCnt ?? 0;
    return {
      list: raw.couponBrandLinkList ?? [],
      totalCnt,
      filterOption: INITIAL_BRAND_LINK.filterOption,
      pagination: { ...INITIAL_BRAND_LINK.pagination, pages: Math.ceil(totalCnt / INITIAL_BRAND_LINK.pagination.pageSize) || 1, totalCnt },
    };
  }, [detailQuery.data?.couponBrandLink]);

  // 폴백 기본값
  const emptyUsageHistory = {
    list: [], totalCnt: 0, summary: { totalCnt: 0 },
    filterOption: INITIAL_USAGE_HISTORY.filterOption, sortOption: INITIAL_USAGE_HISTORY.sortOption,
    pagination: { ...INITIAL_USAGE_HISTORY.pagination, pages: 1, totalCnt: 0 },
  };
  const emptyUnUsageHistory = {
    list: [], totalCnt: 0, filterOption: INITIAL_UN_USAGE_HISTORY.filterOption,
    pagination: { ...INITIAL_UN_USAGE_HISTORY.pagination, pages: 1, totalCnt: 0 },
  };
  const emptyProductLink = {
    list: [], totalCnt: 0, filterOption: INITIAL_PRODUCT_LINK.filterOption,
    pagination: { ...INITIAL_PRODUCT_LINK.pagination, pages: 1, totalCnt: 0 },
  };
  const emptyBrandLink = {
    list: [], totalCnt: 0, filterOption: INITIAL_BRAND_LINK.filterOption,
    pagination: { ...INITIAL_BRAND_LINK.pagination, pages: 1, totalCnt: 0 },
  };

  // 인터랙션 전: detailQuery 데이터 사용, 인터랙션 후: 서브 쿼리 데이터 사용
  const couponUsageHistory = usageHistoryActive
    ? (usageHistoryQuery.data ?? emptyUsageHistory)
    : (initialUsageHistory ?? emptyUsageHistory);

  const couponUnUsageHistory = unUsageHistoryActive
    ? (unUsageHistoryQuery.data ?? emptyUnUsageHistory)
    : (initialUnUsageHistory ?? emptyUnUsageHistory);

  const couponProductLink = productLinkActive
    ? (productLinkQuery.data ?? emptyProductLink)
    : (initialProductLink ?? emptyProductLink);

  const couponBrandLink = brandLinkActive
    ? (brandLinkQuery.data ?? emptyBrandLink)
    : (initialBrandLink ?? emptyBrandLink);

  // 콜백 함수들 (호출 시 서브 쿼리 활성화)
  const getCouponUsageHistoryList = useCallback((data: Record<string, unknown>) => {
    setUsageHistoryActive(true);
    setUsageHistoryOpt((prev) => ({
      filterOption: (data.filterOption as Record<string, unknown>) ?? prev.filterOption,
      sortOption: (data.sortOption as Record<string, unknown>) ?? prev.sortOption,
      pagination: (data.pagination as { pageNum: number; pageSize: number }) ?? prev.pagination,
    }));
  }, []);

  const getCouponUsageHistoryListExcelDownload = useCallback(async () => {
    try {
      const result = await CouponApiV2.getCouponUsageHistoryListExcelDownload({
        couponId,
        filterOption: usageHistoryOpt.filterOption,
      });
      excelDownLoad('쿠폰 사용내역 리스트', (result as any).data.couponUsageHistoryListExcelSheet);
    } catch (error: any) {
      toastMsg.error(error?.message || couponToastMsg.GET_COUPON_USAGE_HISTORY_LIST_EXCEL_DOWNLOAD_REQUEST_ERROR, error);
    }
  }, [couponId, usageHistoryOpt.filterOption]);

  const getCouponUnUsageHistoryList = useCallback((data: Record<string, unknown>) => {
    setUnUsageHistoryActive(true);
    setUnUsageHistoryOpt((prev) => ({
      filterOption: (data.filterOption as Record<string, unknown>) ?? prev.filterOption,
      pagination: (data.pagination as { pageNum: number; pageSize: number }) ?? prev.pagination,
    }));
  }, []);

  const getCouponUnUsageHistoryListExcelDownload = useCallback(async () => {
    try {
      const result = await CouponApiV2.getCouponUnUsageHistoryListExcelDownload({
        couponId,
        filterOption: unUsageHistoryOpt.filterOption,
      });
      excelDownLoad('쿠폰 미사용내역 리스트', (result as any).data.couponUnUsageHistoryListExcelSheet);
    } catch (error: any) {
      toastMsg.error(error?.message || couponToastMsg.GET_COUPON_UN_USAGE_HISTORY_LIST_EXCEL_DOWNLOAD_REQUEST_ERROR, error);
    }
  }, [couponId, unUsageHistoryOpt.filterOption]);

  const getCouponProductLinkList = useCallback((data: Record<string, unknown>) => {
    setProductLinkActive(true);
    setProductLinkOpt((prev) => ({
      filterOption: (data.filterOption as Record<string, unknown>) ?? prev.filterOption,
      pagination: (data.pagination as { pageNum: number; pageSize: number }) ?? prev.pagination,
    }));
  }, []);

  const getCouponBrandLinkList = useCallback((data: Record<string, unknown>) => {
    setBrandLinkActive(true);
    setBrandLinkOpt((prev) => ({
      filterOption: (data.filterOption as Record<string, unknown>) ?? prev.filterOption,
      pagination: (data.pagination as { pageNum: number; pageSize: number }) ?? prev.pagination,
    }));
  }, []);

  const toggleModal = useCallback((payload: Record<string, unknown>) => {
    dispatch(couponActions.toggleModal(payload));
  }, [dispatch]);

  const getProductSubCategoryList = useCallback((payload: Record<string, unknown>) => {
    dispatch(appActions.getProductSubCategoryList(payload));
  }, [dispatch]);

  return {
    isLoading: detailQuery.isLoading,
    couponDetail: {
      couponInfo,
      couponStatistics,
      couponUsageHistory,
      couponUnUsageHistory,
      couponProductLink,
      couponBrandLink,
      mainProductCategorySelectList: detailQuery.data?.mainProductCategorySelectList ?? [],
      subProductCategorySelectList: detailQuery.data?.subProductCategorySelectList ?? { null: [] },
    },
    getCouponUsageHistoryList,
    getCouponUsageHistoryListExcelDownload,
    getCouponUnUsageHistoryList,
    getCouponUnUsageHistoryListExcelDownload,
    getCouponProductLinkList,
    getCouponBrandLinkList,
    toggleModal,
    getProductSubCategoryList,
  };
};
